import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { 
  Sparkles, Target, FileText, Users, Crown, Trophy, Star, Zap,
  Check, Lock, ChevronRight, Flame, Award, Medal
} from 'lucide-react';

const BADGE_ICONS = {
  sparkles: Sparkles,
  'file-text': FileText,
  target: Target,
  users: Users,
  crown: Crown
};

const BrandJourney = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [config, setConfig] = useState(null);
  const [progress, setProgress] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [disciplineLeaderboard, setDisciplineLeaderboard] = useState([]);
  const [activeTab, setActiveTab] = useState('journey');
  const [showBadgeModal, setShowBadgeModal] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [configRes, progressRes, milestonesRes, leaderboardRes, disciplineRes] = await Promise.all([
        axios.get(`${API}/journey/config`),
        axios.get(`${API}/journey/progress`, { headers }),
        axios.get(`${API}/journey/milestones`, { headers }),
        axios.get(`${API}/journey/leaderboard`, { headers }),
        axios.get(`${API}/journey/discipline-leaderboard`, { headers })
      ]);
      
      setConfig(configRes.data);
      setProgress(progressRes.data);
      setMilestones(milestonesRes.data.milestones);
      setLeaderboard(leaderboardRes.data.leaderboard);
      setDisciplineLeaderboard(disciplineRes.data.leaderboard);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const completeStep = async (stepId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/journey/complete-step`, 
        { step_id: stepId },
        { headers }
      );
      
      if (response.data.points_earned > 0) {
        toast.success(t('brandJourney.toasts.pointsEarned', { points: response.data.points_earned }));
        
        // Show badge notifications
        if (response.data.new_badges?.length > 0) {
          response.data.new_badges.forEach(badge => {
            toast.success(t('brandJourney.toasts.badgeUnlocked', { name: badge.name }), {
              duration: 5000,
              icon: '🏆'
            });
          });
        }
        
        // Show level up notification
        if (response.data.new_level) {
          toast.success(t('brandJourney.toasts.levelUp', { level: response.data.new_level.name }), {
            duration: 5000,
            icon: '⭐'
          });
        }
      }
      
      fetchData();
    } catch (error) {
      toast.error(t('brandJourney.toasts.error'));
    }
  };

  const getStepsByCategory = (category) => {
    return config?.steps.filter(s => s.category === category) || [];
  };

  const isStepCompleted = (stepId) => {
    return progress?.completed_steps?.includes(stepId);
  };

  const isBadgeEarned = (badgeId) => {
    return progress?.earned_badges?.includes(badgeId);
  };

  const getBadgeProgress = (badge) => {
    if (!badge.required_steps) return 0;
    const completed = badge.required_steps.filter(s => isStepCompleted(s)).length;
    return Math.round((completed / badge.required_steps.length) * 100);
  };

  const categories = [
    { id: 'foundation', name: t('brandJourney.categories.foundation'), icon: Sparkles, color: '#D4AF37' },
    { id: 'content', name: t('brandJourney.categories.content'), icon: FileText, color: '#E5C585' },
    { id: 'visual', name: t('brandJourney.categories.visual'), icon: Target, color: '#8B7355' },
    { id: 'strategy', name: t('brandJourney.categories.strategy'), icon: Zap, color: '#C9A959' },
    { id: 'community', name: t('brandJourney.categories.community'), icon: Users, color: '#D4AF37' },
    { id: 'mastery', name: t('brandJourney.categories.mastery'), icon: Crown, color: '#FFD700' }
  ];

  if (!config || !progress) {
    return (
      <DashboardLayout title={t('brandJourney.title')}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-pulse text-[#E5C585]">{t('brandJourney.loading')}</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={t('brandJourney.title')}>
      <div className="max-w-6xl mx-auto">
        {/* Progress Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-gold p-8 mb-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-radial from-[#D4AF37]/20 to-transparent rounded-full blur-3xl" />
          
          <div className="relative grid md:grid-cols-3 gap-8 items-center">
            {/* Level & Points */}
            <div className="text-center md:text-left">
              <div className="flex items-center gap-3 mb-2 justify-center md:justify-start">
                <div className="w-8 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
                <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">{t('brandJourney.level')} {progress.current_level}</span>
              </div>
              <h2 className="font-serif text-3xl text-white mb-2">{progress.level_name}</h2>
              <p className="text-[#E5C585] font-serif text-2xl">{progress.total_points} {t('brandJourney.points')}</p>
              {progress.points_to_next_level > 0 && (
                <p className="text-[#52525B] text-sm mt-2">
                  {progress.points_to_next_level} {t('brandJourney.pointsToNextLevel')}
                </p>
              )}
            </div>

            {/* Progress Ring */}
            <div className="flex justify-center">
              <div className="relative w-40 h-40">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="80" cy="80" r="70"
                    stroke="#1C1C1C"
                    strokeWidth="8"
                    fill="none"
                  />
                  <circle
                    cx="80" cy="80" r="70"
                    stroke="#D4AF37"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 70}`}
                    strokeDashoffset={`${2 * Math.PI * 70 * (1 - progress.completion_percentage / 100)}`}
                    strokeLinecap="round"
                    className="transition-all duration-1000"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="font-serif text-3xl text-white">{progress.completion_percentage}%</span>
                  <span className="text-[#52525B] text-xs">{t('brandJourney.complete')}</span>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-[#0A0A0A]/50 border border-[#27272A]">
                <Flame className="text-[#D4AF37] mx-auto mb-2" size={24} />
                <span className="font-serif text-2xl text-white">{progress.streak_days}</span>
                <p className="text-[#52525B] text-xs">{t('brandJourney.dayStreak')}</p>
              </div>
              <div className="text-center p-4 bg-[#0A0A0A]/50 border border-[#27272A]">
                <Target className="text-[#E5C585] mx-auto mb-2" size={24} />
                <span className="font-serif text-2xl text-white">{progress.discipline_points || 0}</span>
                <p className="text-[#52525B] text-xs">{t('brandJourney.disciplinePoints')}</p>
              </div>
              <div className="text-center p-4 bg-[#0A0A0A]/50 border border-[#27272A]">
                <Trophy className="text-[#E5C585] mx-auto mb-2" size={24} />
                <span className="font-serif text-2xl text-white">{progress.earned_badges?.length || 0}</span>
                <p className="text-[#52525B] text-xs">{t('brandJourney.tabs.badges')}</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-[#1C1C1C] pb-4 flex-wrap">
          {[
            { id: 'journey', label: t('brandJourney.tabs.journey'), icon: Target },
            { id: 'badges', label: t('brandJourney.tabs.badges'), icon: Award },
            { id: 'leaderboard', label: t('brandJourney.tabs.leaderboard'), icon: Trophy },
            { id: 'discipline', label: t('brandJourney.tabs.discipline'), icon: Flame }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 flex items-center gap-2 transition-all ${
                  activeTab === tab.id
                    ? 'text-[#E5C585] bg-[#E5C585]/5 border-b-2 border-[#D4AF37]'
                    : 'text-[#71717A] hover:text-white'
                }`}
                data-testid={`journey-tab-${tab.id}`}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Journey View */}
        {activeTab === 'journey' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            {categories.map((category, catIndex) => {
              const Icon = category.icon;
              const steps = getStepsByCategory(category.id);
              if (steps.length === 0) return null;
              
              const completedInCategory = steps.filter(s => isStepCompleted(s.id)).length;
              
              return (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: catIndex * 0.1 }}
                  className="card-premium p-6"
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center border" style={{ borderColor: category.color }}>
                        <Icon style={{ color: category.color }} size={20} />
                      </div>
                      <div>
                        <h3 className="font-serif text-xl text-white">{category.name}</h3>
                        <p className="text-[#52525B] text-sm">{completedInCategory}/{steps.length} {t('brandJourney.completed')}</p>
                      </div>
                    </div>
                    <div className="w-24 h-2 bg-[#1C1C1C] overflow-hidden">
                      <div 
                        className="h-full transition-all duration-500"
                        style={{ 
                          width: `${(completedInCategory / steps.length) * 100}%`,
                          backgroundColor: category.color 
                        }}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    {steps.map((step) => {
                      const completed = isStepCompleted(step.id);
                      return (
                        <div 
                          key={step.id}
                          className={`p-4 border transition-all ${
                            completed 
                              ? 'bg-[#D4AF37]/5 border-[#D4AF37]/30' 
                              : 'bg-[#0A0A0A] border-[#27272A] hover:border-[#3F3F46]'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                {completed ? (
                                  <Check className="text-[#D4AF37]" size={16} />
                                ) : (
                                  <div className="w-4 h-4 border border-[#27272A]" />
                                )}
                                <h4 className={`font-medium ${completed ? 'text-[#E5C585]' : 'text-white'}`}>
                                  {step.name}
                                </h4>
                              </div>
                              <p className="text-[#52525B] text-sm ml-6">{step.description}</p>
                            </div>
                            <span className={`text-sm font-serif ${completed ? 'text-[#D4AF37]' : 'text-[#52525B]'}`}>
                              +{step.points}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* Badges View */}
        {activeTab === 'badges' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {config.badges.map((badge, index) => {
                const earned = isBadgeEarned(badge.id);
                const progressPercent = getBadgeProgress(badge);
                const Icon = BADGE_ICONS[badge.icon] || Award;
                
                return (
                  <motion.div
                    key={badge.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => setShowBadgeModal(badge)}
                    className={`card-premium p-6 cursor-pointer transition-all hover:scale-[1.02] ${
                      earned ? 'border-[#D4AF37]/50' : ''
                    }`}
                  >
                    <div className="text-center">
                      <div 
                        className={`w-20 h-20 mx-auto mb-4 flex items-center justify-center border-2 ${
                          earned ? '' : 'opacity-40 grayscale'
                        }`}
                        style={{ borderColor: badge.color }}
                      >
                        <Icon size={36} style={{ color: badge.color }} />
                      </div>
                      
                      <h3 className={`font-serif text-xl mb-2 ${earned ? 'text-white' : 'text-[#52525B]'}`}>
                        {badge.name}
                      </h3>
                      
                      <p className="text-[#52525B] text-sm mb-4">{badge.description}</p>
                      
                      {earned ? (
                        <span className="inline-block px-3 py-1 bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-accent tracking-wider">
                          {t('brandJourney.earned')}
                        </span>
                      ) : (
                        <div>
                          <div className="w-full h-2 bg-[#1C1C1C] overflow-hidden mb-2">
                            <div 
                              className="h-full transition-all"
                              style={{ width: `${progressPercent}%`, backgroundColor: badge.color }}
                            />
                          </div>
                          <span className="text-[#52525B] text-xs">{progressPercent}% {t('brandJourney.progress')}</span>
                        </div>
                      )}
                      
                      <p className="text-[#E5C585] text-sm mt-3">+{badge.points_reward} {t('brandJourney.points')}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Leaderboard View */}
        {activeTab === 'leaderboard' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="card-premium p-6">
              <div className="flex items-center gap-3 mb-6">
                <Trophy className="text-[#D4AF37]" size={24} />
                <h3 className="font-serif text-xl text-white">{t('brandJourney.leaderboardTitle')}</h3>
              </div>
              
              {leaderboard.length > 0 ? (
                <div className="space-y-3">
                  {leaderboard.map((entry, index) => (
                    <div 
                      key={entry.user_id}
                      className={`flex items-center justify-between p-4 ${
                        entry.user_id === user?.user_id ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30' : 'bg-[#0A0A0A] border border-[#27272A]'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <span className={`w-8 h-8 flex items-center justify-center font-serif ${
                          index === 0 ? 'bg-[#D4AF37] text-black' :
                          index === 1 ? 'bg-[#A1A1AA] text-black' :
                          index === 2 ? 'bg-[#8B7355] text-white' :
                          'bg-[#27272A] text-white'
                        }`}>
                          {index + 1}
                        </span>
                        
                        <div className="flex items-center gap-3">
                          {entry.picture ? (
                            <img src={entry.picture} alt={entry.name} className="w-10 h-10 rounded-full" />
                          ) : (
                            <div className="w-10 h-10 bg-[#1C1C1C] rounded-full flex items-center justify-center">
                              <span className="text-[#E5C585] font-serif">{entry.name?.charAt(0)}</span>
                            </div>
                          )}
                          <div>
                            <p className="text-white">{entry.name}</p>
                            <p className="text-[#D4AF37] text-xs">{entry.level_name}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        {entry.total_points > 0 ? (
                          <>
                            <span className="font-serif text-xl text-[#E5C585]">{entry.total_points}</span>
                            <p className="text-[#52525B] text-xs">{t('brandJourney.points')}</p>
                          </>
                        ) : (
                          <span className="text-[#D4AF37] text-xs font-accent">{t('brandJourney.readyToStart')}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-[#52525B] py-8">
                  {t('brandJourney.noParticipants')}
                </p>
              )}
            </div>
          </motion.div>
        )}

        {/* Discipline Leaderboard View - Phase 1 */}
        {activeTab === 'discipline' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="card-premium p-6">
              <div className="flex items-center gap-3 mb-6">
                <Flame className="text-[#D4AF37]" size={24} />
                <div>
                  <h3 className="font-serif text-xl text-white">{t('brandJourney.disciplineTitle')}</h3>
                  <p className="text-[#52525B] text-sm">{t('brandJourney.disciplineSubtitle')}</p>
                </div>
              </div>
              
              {/* Your discipline stats */}
              <div className="mb-6 p-4 bg-[#D4AF37]/5 border border-[#D4AF37]/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A1A1AA] text-xs font-accent tracking-wider mb-1">{t('brandJourney.yourDiscipline')}</p>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Target className="text-[#D4AF37]" size={16} />
                        <span className="font-serif text-xl text-white">{progress.discipline_points || 0}</span>
                        <span className="text-[#52525B] text-xs">{t('brandJourney.points')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Flame className="text-[#E5C585]" size={16} />
                        <span className="font-serif text-xl text-white">{progress.streak_days || 0}</span>
                        <span className="text-[#52525B] text-xs">{t('brandJourney.dayStreak')}</span>
                      </div>
                    </div>
                  </div>
                  {/* Top X% indicator */}
                  {progress.discipline_percentile !== undefined && progress.discipline_percentile !== null && (
                    <div className="text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <Crown className="text-[#D4AF37]" size={18} />
                        <span className="font-serif text-xl text-[#D4AF37]">Top {100 - progress.discipline_percentile}%</span>
                      </div>
                      <p className="text-[#52525B] text-xs mt-1">{t('brandJourney.disciplineRanking')}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {disciplineLeaderboard.length > 0 ? (
                <div className="space-y-3">
                  {disciplineLeaderboard.map((entry, index) => (
                    <div 
                      key={entry.user_id}
                      className={`flex items-center justify-between p-4 ${
                        entry.user_id === user?.user_id ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30' : 'bg-[#0A0A0A] border border-[#27272A]'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <span className={`w-8 h-8 flex items-center justify-center font-serif ${
                          index === 0 ? 'bg-[#D4AF37] text-black' :
                          index === 1 ? 'bg-[#A1A1AA] text-black' :
                          index === 2 ? 'bg-[#8B7355] text-white' :
                          'bg-[#27272A] text-white'
                        }`}>
                          {index + 1}
                        </span>
                        
                        <div className="flex items-center gap-3">
                          {entry.picture ? (
                            <img src={entry.picture} alt={entry.name} className="w-10 h-10 rounded-full" />
                          ) : (
                            <div className="w-10 h-10 bg-[#1C1C1C] rounded-full flex items-center justify-center">
                              <span className="text-[#E5C585] font-serif">{entry.name?.charAt(0)}</span>
                            </div>
                          )}
                          <div>
                            <p className="text-white">{entry.name}</p>
                            {entry.streak_days > 0 && (
                              <div className="flex items-center gap-1 text-[#D4AF37] text-xs">
                                <Flame size={10} />
                                <span>{entry.streak_days} {t('brandJourney.consecutiveDays')}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        {entry.discipline_points > 0 ? (
                          <>
                            <span className="font-serif text-xl text-[#E5C585]">{entry.discipline_points}</span>
                            <p className="text-[#52525B] text-xs">{t('brandJourney.disciplinePoints')}</p>
                          </>
                        ) : (
                          <span className="text-[#52525B] text-xs font-accent">{t('brandJourney.noActionsYet')}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-[#52525B] py-8">
                  {t('brandJourney.noParticipants')}
                </p>
              )}
            </div>
          </motion.div>
        )}

        {/* Badge Modal */}
        <AnimatePresence>
          {showBadgeModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowBadgeModal(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={e => e.stopPropagation()}
                className="card-luxury p-8 max-w-md w-full text-center"
              >
                {(() => {
                  const Icon = BADGE_ICONS[showBadgeModal.icon] || Award;
                  const earned = isBadgeEarned(showBadgeModal.id);
                  
                  return (
                    <>
                      <div 
                        className={`w-24 h-24 mx-auto mb-6 flex items-center justify-center border-2 ${
                          earned ? '' : 'opacity-40 grayscale'
                        }`}
                        style={{ borderColor: showBadgeModal.color }}
                      >
                        <Icon size={48} style={{ color: showBadgeModal.color }} />
                      </div>
                      
                      <h3 className="font-serif text-2xl text-white mb-2">{showBadgeModal.name}</h3>
                      <p className="text-[#A1A1AA] mb-6">{showBadgeModal.description}</p>
                      
                      {showBadgeModal.required_steps && (
                        <div className="text-left mb-6">
                          <p className="text-[#52525B] text-xs font-accent tracking-wider mb-3">{t('brandJourney.requirements')}</p>
                          <div className="space-y-2">
                            {showBadgeModal.required_steps.map(stepId => {
                              const step = config.steps.find(s => s.id === stepId);
                              const completed = isStepCompleted(stepId);
                              return (
                                <div key={stepId} className="flex items-center gap-2">
                                  {completed ? (
                                    <Check className="text-[#D4AF37]" size={14} />
                                  ) : (
                                    <div className="w-3.5 h-3.5 border border-[#27272A]" />
                                  )}
                                  <span className={completed ? 'text-[#A1A1AA]' : 'text-[#52525B]'}>
                                    {step?.name}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {showBadgeModal.required_badges && (
                        <div className="text-left mb-6">
                          <p className="text-[#52525B] text-xs font-accent tracking-wider mb-3">{t('brandJourney.requiresBadges')}</p>
                          <div className="flex flex-wrap gap-2">
                            {showBadgeModal.required_badges.map(badgeId => {
                              const badge = config.badges.find(b => b.id === badgeId);
                              const hasIt = isBadgeEarned(badgeId);
                              return (
                                <span 
                                  key={badgeId}
                                  className={`px-3 py-1 text-sm ${
                                    hasIt ? 'bg-[#D4AF37]/20 text-[#E5C585]' : 'bg-[#1C1C1C] text-[#52525B]'
                                  }`}
                                >
                                  {badge?.name}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      <p className="text-[#E5C585]">{t('brandJourney.reward')}: +{showBadgeModal.points_reward} {t('brandJourney.points')}</p>
                      
                      <Button 
                        onClick={() => setShowBadgeModal(null)}
                        className="btn-outline-gold w-full mt-6 py-3"
                      >
                        {t('brandJourney.close')}
                      </Button>
                    </>
                  );
                })()}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
};

export default BrandJourney;
